import { useMemo, useState, type ReactNode } from 'react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import type { AppWorkSummary } from '@shared/types/thread-work';
import { AppWorkRow } from '@/components/apps/AppWorkRow';
import { RitualCard } from '@/components/rituals/RitualCard';
import type { DueRitual } from '@/lib/workspace-rituals';
import { cn } from '@/lib/utils';
import { LivingWorkRow, type LivingWorkHandlers, type RowPlan } from './LivingWorkRow';
import { TodayLaunchRow } from './TodayLaunchRow';
import { TodayHandoffRow } from './TodayHandoffRow';
import { type HandoffsView, useTeam } from '@/lib/work-handoffs';
import { useFileRuns } from '@/lib/work-runs';
import { TodayCompanyGroup } from './TodayCompanyGroup';
import { TodayDoneRow } from './TodayDoneRow';
import { groupByCompany, type FileEntry } from './today-companies';
import { currentTitle, lastDoneLine } from './work-done';
import { TodayReview } from './TodayReview';
import { projectActivityLivingWorks } from './thread-living-model';
import { buildTodayRows, searchTodayRows } from './today-pipeline';
import { currentEntries } from './team-week';
import { placeRituals } from './today-rituals';
import { plannedRowNote, planTodayRows, reviewQueue, type PlannedTodayRow } from './today-horizons';

type TodayView = 'today' | 'later' | 'objectives';

// `empty:hidden`: no border drawn around a list with no row at all.
const LIST_CLASS = 'space-y-2 empty:hidden sm:space-y-0 sm:divide-y sm:divide-domino-cardBorder sm:rounded-lg sm:border sm:border-domino-cardBorder sm:bg-domino-white';


/**
 * Mon travail (« Tâche, séance, relais », 21/10/2026): one file, one row per
 * task, with no headings. Tasks passed to me first (relais), then rituals (an
 * app ritual carries its app's launches), then engagements and Domino's
 * suggestions in the read model's order, account and app work alike; what the
 * work belongs to is in each row's first cell. One launch of an app is one
 * task; one company is one line (« La chaîne des comptes »), each task saying
 * what was last done on it, and what I finished today stays until tomorrow.
 * « Planifié » holds dated work, then the backlog.
 */
export const TodayWorkList = ({
  subjects,
  apps = [],
  search = '',
  toolbar,
  rituals = [],
  today,
  onRitualChange,
  handoffs,
  objectives,
  onAppWorkChange,
  ...handlers
}: LivingWorkHandlers & {
  subjects: ActivitySubjectCard[];
  apps?: AppWorkSummary[];
  search?: string;
  /** Rendered above the list, on the right (search field). */
  toolbar?: ReactNode;
  /** First rows of the list: my rituals due today; an app ritual carries its app's launches. */
  rituals?: DueRitual[];
  /** Today in the person's calendar (YYYY-MM-DD), for the rituals. */
  today?: string;
  onRitualChange?: (ritualId: string, occurrence: DueRitual['occurrence']) => void;
  /** Relais: tasks passed to me come first; tasks I passed leave my file while open. */
  handoffs?: HandoffsView;
  /** Content of the « Objectifs » tab; no tab without it. */
  objectives?: ReactNode;
  onAppWorkChange?: () => void;
}) => {
  const [view, setView] = useState<TodayView>('today');
  const [reviewing, setReviewing] = useState(false);
  const now = useMemo(() => new Date(), [subjects, apps]);
  const planned = useMemo(
    () => planTodayRows(searchTodayRows(buildTodayRows(projectActivityLivingWorks(subjects), apps), search), now)
      .filter((item) => !handoffs?.passedTopicIds.has(item.row.topic.id)),
    [subjects, apps, search, now, handoffs]
  );
  const todayItems = planned.filter((item) => item.bucket === 'today');
  const query = search.trim().toLocaleLowerCase('fr');
  const shownRituals = query ? rituals.filter(({ ritual }) => ritual.title.toLocaleLowerCase('fr').includes(query)) : rituals;
  const incoming = (handoffs?.incoming ?? []).filter((handoff) => !query || handoff.title.toLocaleLowerCase('fr').includes(query));
  const { entries: placed, launchOf } = placeRituals(currentEntries(todayItems), shownRituals);
  const todayEntries = groupByCompany(placed);
  const runs = useFileRuns(planned.map((item) => item.row.topic.id));
  const team = useTeam();
  const nameOf = (userId: string) => team?.members.find((member) => member.userId === userId)?.name ?? 'un collègue';
  const doneToday = runs.today.filter((run) => !query || run.label.toLocaleLowerCase('fr').includes(query));
  const plannedItems = [
    ...planned.filter((item) => item.bucket === 'later')
      .sort((left, right) => (left.row.topic.plannedFor ?? '').localeCompare(right.row.topic.plannedFor ?? '')),
    ...planned.filter((item) => item.bucket === 'someday'),
  ];
  const review = reviewQueue(planned);

  const render = (item: PlannedTodayRow, nested = false) => {
    const last = runs.lastByTopic.get(item.row.topic.id);
    const title = currentTitle(item.row.topic, last);
    const plan: RowPlan = {
      commitment: item.commitment, bucket: item.bucket, plannable: item.plannable, note: plannedRowNote(item, now),
      reply: handoffs?.replies.get(item.row.topic.id) ?? null,
      title: title !== item.row.topic.title ? title : null,
      done: last ? { text: lastDoneLine(last, now, team?.me ?? null, nameOf), failed: last.status === 'failed' } : null,
      nested,
    };
    const { row } = item;
    return row.kind === 'account'
      ? <LivingWorkRow key={row.key} item={row.item} plan={plan} {...handlers} />
      : <AppWorkRow key={row.key} flowId={row.app.flowId} flowTitle={row.app.flowTitle} topic={row.topic} onChange={onAppWorkChange} plan={plan} />;
  };
  const renderEntry = (entry: FileEntry) => entry.kind === 'row'
    ? render(entry.item)
    : entry.kind === 'company'
      ? <TodayCompanyGroup key={entry.key} items={entry.items} render={render} />
      : <TodayLaunchRow key={entry.launch.key} launch={entry.launch} onChange={onAppWorkChange} />;
  const tab = (value: TodayView, label: string, count: number | null) => (
    <button
      type="button"
      aria-pressed={view === value}
      onClick={() => setView(value)}
      className={cn('border-b-2 py-1.5 text-sm', view === value ? 'border-domino-aubergine font-semibold text-domino-ink' : 'border-transparent text-domino-inkSoft hover:text-domino-ink')}
    >
      {label}{count ? <span className="ml-1 tabular-nums font-normal text-domino-inkSoft">{count}</span> : null}
    </button>
  );
  const searchMiss = search.trim()
    ? <p className="border-t border-domino-cardBorder py-8 text-sm text-domino-inkSoft">Aucun travail ne correspond à cette recherche.</p>
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 border-b border-domino-cardBorder">
        <div className="flex gap-4">
          {tab('today', 'En cours', null)}
          {tab('later', 'Planifié', plannedItems.length)}
          {objectives && tab('objectives', 'Objectifs', null)}
        </div>
        {view !== 'objectives' && review.length > 0 && (
          <button type="button" onClick={() => setReviewing(true)} className="py-1.5 text-sm text-domino-aubergine hover:underline">
            Revue <span className="tabular-nums">{review.length}</span>
          </button>
        )}
      </div>
      {view === 'objectives' ? objectives : (
        <>
          {toolbar && <div className="flex justify-end"><div className="w-full sm:w-72">{toolbar}</div></div>}
          {view === 'today' ? (
            <>
              <section aria-label="En cours" className={LIST_CLASS}>
                {incoming.map((handoff) => (
                  <TodayHandoffRow key={handoff.id} handoff={handoff} fromName={handoffs!.nameOf(handoff.fromUserId)} onChange={onAppWorkChange} />
                ))}
                {shownRituals.map((entry) => (
                  <RitualCard key={entry.ritual.id} entry={entry} today={today ?? entry.occursOn} launch={launchOf(entry.ritual.id)}
                    onChange={(occurrence) => onRitualChange?.(entry.ritual.id, occurrence)} />
                ))}
                {todayEntries.map(renderEntry)}
                {doneToday.map((run) => <TodayDoneRow key={run.id} run={run} context={run.context} now={now} />)}
              </section>
              {todayEntries.length + shownRituals.length + incoming.length + doneToday.length === 0 && searchMiss}
            </>
          ) : plannedItems.length === 0 ? (
            searchMiss ?? <p className="border-t border-domino-cardBorder py-8 text-sm text-domino-inkSoft">Rien de planifié.</p>
          ) : (
            <section aria-label="Planifié" className={LIST_CLASS}>{plannedItems.map((item) => render(item))}</section>
          )}
        </>
      )}
      {reviewing && <TodayReview items={review} onClose={() => setReviewing(false)} />}
    </div>
  );
};
